import { ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  MAX_BLOCKS_PER_DAY,
  MAX_BLOCK_HOURS,
  MAX_DAILY_WORK_HOURS,
  WorkHoursViolation,
  formatBlockRange,
  getHourState,
  toWorkBlocks,
} from './work-blocks';

const WorkHourItem: React.FC<{
  label: string;
  value: number;
  active: boolean;
  blockedReason?: string;
  disabled?: boolean;
  handleWorkHoursChange(value: number): () => void;
}> = ({ label, value, active, blockedReason, disabled, handleWorkHoursChange }) => {
  const { classes, cx } = useStyles();
  const blocked = !!blockedReason;

  const button = (
    <ButtonBase
      className={cx(classes.workHour, active ? 'active' : null, disabled ? 'disabled' : null, blocked ? 'blocked' : null)}
      onClick={handleWorkHoursChange(value)}
      disableRipple={true}
      disabled={disabled || blocked}
    >
      {label}
    </ButtonBase>
  );

  // A disabled button fires no events, so the tooltip needs a wrapper to hover on.
  return blocked ? (
    <Tooltip title={blockedReason} arrow>
      <span className={classes.tooltipWrapper}>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

interface Props {
  workHours: number[];
  disabled?: boolean;

  handleWorkHoursChange(value: number): () => void;
}

const WorkHoursSelect: React.FC<Props> = ({ workHours, disabled, handleWorkHoursChange }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const allWorkHours = Array.from({ length: 24 }, (item, index) => index);

  const violationText: Record<WorkHoursViolation, string> = {
    maxDailyHours: t('workHoursBlockedMaxDaily', { max: MAX_DAILY_WORK_HOURS }),
    maxBlocks: t('workHoursBlockedMaxBlocks', { max: MAX_BLOCKS_PER_DAY }),
    maxBlockLength: t('workHoursBlockedMaxLength', { max: MAX_BLOCK_HOURS }),
    invalidHour: '',
  };

  const renderHour = (hour: number) => {
    const state = getHourState(workHours, hour);
    return (
      <WorkHourItem
        key={hour}
        label={`${hour}`}
        value={hour}
        active={state.active}
        blockedReason={!disabled && !state.selectable && state.violation ? violationText[state.violation] : undefined}
        disabled={disabled}
        handleWorkHoursChange={handleWorkHoursChange}
      />
    );
  };

  const blocks = toWorkBlocks(workHours);

  return (
    <Stack gap={2} flexWrap={'wrap'}>
      <Stack gap={0.5}>
        <Stack direction={'row'} gap={0.5} flexWrap={'wrap'}>
          {_.map(_.take(allWorkHours, 12), renderHour)}
        </Stack>
        <Stack direction={'row'} gap={0.5} flexWrap={'wrap'}>
          {_.map(_.takeRight(allWorkHours, 12), renderHour)}
        </Stack>
      </Stack>

      {blocks.length === 0 ? (
        <Typography className={classes.summaryEmpty}>{t('workHoursNoneSelected')}</Typography>
      ) : (
        <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
          {blocks.map((block, index) => (
            <Typography key={block.startHour} className={classes.summary}>
              {t('workHoursWindow', { index: index + 1, range: formatBlockRange(block) })}
            </Typography>
          ))}
          <Typography className={classes.summaryTotal}>
            {t('workHoursUsed', { used: workHours.length, max: MAX_DAILY_WORK_HOURS })}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  workHour: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '10px',
    backgroundColor: '#FFF',
    color: theme.palette.greys.light,
    fontSize: theme.typography.pxToRem(16),
    width: '40px',
    height: '30px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      background: theme.palette.action.hover,
    },
    '&.active': {
      background: theme.palette.primary.main,
      border: `2px solid ${theme.palette.primary.main}`,
      color: '#FFF',
    },
    '&.blocked': {
      background: theme.palette.greys.lighter,
      color: theme.palette.greys.light,
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&.disabled': {
      '&.active': {
        background: theme.palette.greys.lighter,
        color: '#FFF',
      },
    },
  },
  tooltipWrapper: {
    display: 'inline-flex',
  },
  summary: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  summaryTotal: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  summaryEmpty: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.warning.dark,
  },
}));

export default WorkHoursSelect;
